/**
 * PATH: packages/email-hub/src/feature/components/rich-text/signatureBlock.ts
 * FILE_ID: EMAILHUB-RICHTEXT-SIGBLOCK-001
 * PURPOSE: Pure I→O helpers that let a real, table-based email signature
 *          survive the ProseMirror editor UNTOUCHED. The signature HTML is
 *          carried inside ONE opaque `<div data-qte-sig="…">` element so the
 *          editor never parses its internal layout tables (which it would
 *          normalise into a rectangular grid — shattering the banner row,
 *          padding empty columns and dropping the logo).
 * WHY:     Operator pasted a Bravada Outlook signature into the signature
 *          editor and it "copied over" mangled (banner split into ~20 cells,
 *          logo gone) — BUG-2026-06-29-EMAIL-SIGNATURE-LAYOUT-MANGLED.
 *          prosemirror-tables CANNOT represent arbitrary email-layout tables,
 *          so the only faithful path is to NOT route the signature through the
 *          table schema at all. This is the same opaque-atom trick the Kanban
 *          card chip uses (kanbanCardNode.tsx).
 * FLOW:
 *   raw signature html ──encodeSignatureBlock──▶ <div data-qte-sig="enc">
 *   editor / drafts round-trip the opaque div verbatim (no table parse)
 *   send / preview ──expandSignatureBlocks──▶ raw signature html (faithful)
 * RELATED:
 *   - signatureBlockNode.tsx: the TipTap atom node + NodeView (faithful preview)
 *   - emailHtml.ts: toEmailHtml expands the block verbatim on send
 */

/** The marker attribute that flags an opaque signature block. */
export const SIGNATURE_BLOCK_ATTR = 'data-qte-sig'

/** Encode signature HTML into an HTML-attribute-safe string.
 *  `encodeURIComponent` escapes every character that is unsafe in a quoted
 *  attribute (`"`, `<`, `>`, `&`, space, …) and leaves the base64 alphabet of
 *  an inline `data:` image largely intact, so a logo travels with no quote
 *  breakage and no recursion/stack risk on large bodies. Pure: html → enc. */
export function encodeSignatureBlock(html: string): string {
  try {
    return encodeURIComponent(html || '')
  } catch {
    return ''
  }
}

/** Inverse of {@link encodeSignatureBlock}. Never throws — a malformed value
 *  (hand-edited draft, truncated cache) decodes to '' so the block renders
 *  empty rather than crashing the editor. Pure: enc → html. */
export function decodeSignatureBlock(enc: string | null | undefined): string {
  if (!enc) return ''
  try {
    return decodeURIComponent(enc)
  } catch {
    return ''
  }
}

/**
 * Should this signature HTML be carried as an opaque block (preserved
 * byte-for-byte) rather than parsed by the editor?
 *
 * TRUE for real layout signatures (multi-row tables, colspans, nested tables,
 * embedded logos, coloured cells) that the table schema would mangle. FALSE
 * for plain typed signatures (no table) and the Quotech-branded DEFAULT
 * (foreman beaver) — a simple, single-row table that already round-trips
 * cleanly, so it keeps its existing INLINE, editable behaviour (zero
 * regression for every operator who hasn't pasted their own layout).
 * Pure: html → boolean.
 */
export function shouldPreserveSignatureLayout(html: string | null | undefined): boolean {
  const s = html || ''
  // The branded default renders fine inline; never opaque-wrap it.
  if (/data-quotech-default-sig/i.test(s)) return false
  // ANY embedded image must travel opaque (BUG-2026-07-07-EMAIL-SIG-IMAGE-
  // DROPPED): a copy-pasted signature is often divs + <img> with NO table,
  // and routing it through the editor schema re-parents/normalises the
  // markup so the logo can be hoisted or dropped before send. Byte-for-byte
  // carriage is the only faithful path for images, table or not.
  if (/<img\b/i.test(s)) return true
  if (!/<table[\s>]/i.test(s)) return false
  const rowCount = (s.match(/<tr[\s>]/gi) || []).length
  return (
    /\b(?:colspan|rowspan)\s*=/i.test(s)
    || /<table[\s>][\s\S]*<table[\s>]/i.test(s) // nested table
    || /background(?:-color)?\s*[:=]/i.test(s)
    || rowCount >= 3
  )
}

/** Wrap signature HTML for SEEDING into the editor: an opaque block when it's
 *  a real layout (so the table schema can't touch it), otherwise the HTML
 *  unchanged (plain/typed signatures + the beaver default keep working exactly
 *  as before). Blank in → blank out. Pure: html → html. */
export function wrapSignatureLayout(html: string | null | undefined): string {
  const s = html || ''
  if (!s.trim()) return ''
  if (!shouldPreserveSignatureLayout(s)) return s
  return `<div ${SIGNATURE_BLOCK_ATTR}="${encodeSignatureBlock(s)}"></div>`
}

/** Always wrap the HTML as an opaque block (no heuristic). Used by the
 *  signature editor's paste handler, where the operator is explicitly pasting
 *  a formatted signature and we want to preserve it verbatim regardless of how
 *  simple/complex the table is. Blank in → blank out. Pure: html → html. */
export function forceWrapSignatureBlock(html: string | null | undefined): string {
  const s = html || ''
  if (!s.trim()) return ''
  return `<div ${SIGNATURE_BLOCK_ATTR}="${encodeSignatureBlock(s)}"></div>`
}

/**
 * Generic opaque-block expansion: replace every `<div ATTR="enc"></div>` with
 * its decoded raw HTML. Shared by the signature block AND the quoted-original
 * block (quoteBlock.ts) so both atoms round-trip through ONE proven path.
 * DOM-based when available (robust to attribute ordering); regex fallback for
 * non-DOM environments. Pure: (html, attr) → html.
 */
export function expandBlocksByAttr(html: string | null | undefined, attr: string): string {
  const s = html || ''
  if (!s.includes(attr)) return s
  const blockRe = new RegExp(`<div\\b[^>]*\\s${attr}="([^"]*)"[^>]*>\\s*</div>`, 'gi')
  if (typeof DOMParser === 'undefined') {
    return s.replace(blockRe, (_m, enc) => decodeSignatureBlock(enc))
  }
  try {
    const doc = new DOMParser().parseFromString(`<body>${s}</body>`, 'text/html')
    doc.querySelectorAll(`[${attr}]`).forEach((el) => {
      const raw = decodeSignatureBlock(el.getAttribute(attr))
      const frag = doc.createRange().createContextualFragment(raw)
      el.replaceWith(frag)
    })
    return doc.body.innerHTML
  } catch {
    return s.replace(blockRe, (_m, enc) => decodeSignatureBlock(enc))
  }
}

/**
 * Replace every opaque signature block with its decoded raw HTML. Used by the
 * send serialiser (so the recipient gets the faithful signature), by the
 * signature editor on SAVE (so the stored value is raw, reusable HTML), and by
 * any read-only preview. Pure: html → html.
 */
export function expandSignatureBlocks(html: string | null | undefined): string {
  return expandBlocksByAttr(html, SIGNATURE_BLOCK_ATTR)
}

/** True when the HTML contains at least one opaque signature block. */
export function hasSignatureBlock(html: string | null | undefined): boolean {
  return (html || '').includes(SIGNATURE_BLOCK_ATTR)
}
